// Central badge icon registry + renderer (ACHIEVEMENTS-IMPLEMENTATION-PLAN-2026-08
// section 7.1). Previously this map was duplicated in ProfileBadgeRow,
// BadgesCard, and profile/badges/page.tsx — any badge whose icon was missing
// from one copy silently rendered as Award. Import from here only.
//
// Hook-free on purpose: imported by both server and client components.

import {
  Award,
  Binoculars,
  CircleCheck,
  Clapperboard,
  Crown,
  Eye,
  Film,
  Flame,
  Heart,
  Images,
  MessageCircle,
  MessagesSquare,
  PenLine,
  Share2,
  ShieldCheck,
  Sparkles,
  Star,
  ThumbsUp,
  Trophy,
  Twitter,
  UserPlus,
  Users,
} from 'lucide-react';

export const BADGE_ICONS: Record<string, typeof Award> = {
  Award,
  Binoculars,
  CircleCheck,
  Clapperboard,
  Crown,
  Eye,
  Film,
  Flame,
  Heart,
  Images,
  MessageCircle,
  MessagesSquare,
  PenLine,
  Share2,
  ShieldCheck,
  Sparkles,
  Star,
  ThumbsUp,
  Trophy,
  Twitter,
  UserPlus,
  Users,
};

/**
 * Renders a badge visual. Custom artwork (imageUrl) is used at >= 24px where
 * it survives; below that (the profile pill row) the Lucide icon always wins
 * because 14px art is mud (plan D8). Falls back to Award for unknown icons.
 */
export function BadgeArt({
  icon,
  imageUrl,
  sizePx,
  className = '',
}: {
  icon: string;
  imageUrl?: string | null;
  sizePx: number;
  className?: string;
}) {
  if (imageUrl && sizePx >= 24) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- tiny fixed-size icon; next/image adds nothing here
      <img
        src={imageUrl}
        alt=""
        width={sizePx}
        height={sizePx}
        loading="lazy"
        style={{ width: sizePx, height: sizePx }}
        className="rounded-full object-cover shrink-0"
      />
    );
  }
  const I = BADGE_ICONS[icon] ?? Award;
  return <I className={className} strokeWidth={1.75} />;
}

/** "Held by 4.2% of active members" (null-safe). */
export function rarityLabel(ownedPercent?: number | null): string | null {
  if (ownedPercent == null) return null;
  return `Held by ${ownedPercent}% of active members`;
}
